import cfiLogic from '../../cfiLogic';

/**
 * Validates input parameters for character offset calculation
 * @param {BookDocumentInformation} bookDocumentInformation
 * @param {Document} frameDocument
 * @param {UserContentHighlight} selectedHighlight
 * @throws {Error} If any required parameter is missing or invalid
 */
const validateInputs = (
  bookDocumentInformation,
  frameDocument,
  selectedHighlight,
) => {
  if (!bookDocumentInformation) {
    throw new Error('bookDocumentInformation is required');
  }

  if (!frameDocument) {
    throw new Error('frameDocument is required');
  }

  if (!selectedHighlight) {
    throw new Error('selectedHighlight is required');
  }

  if (!Array.isArray(bookDocumentInformation.wordObjects)) {
    throw new Error('bookDocumentInformation.wordObjects must be an array');
  }
};

/**
 * Extracts CFI and snippet information from a highlight
 * @param {UserContentHighlight} selectedHighlight
 * @returns {{cfi: string|null, snippet: string|null, firstPartOfSnippet: string|null}}
 */
const extractHighlightInfo = (selectedHighlight) => {
  const cfi = selectedHighlight?.bookLocation?.cfi || null;
  const snippet = selectedHighlight?.snippet || null;
  const firstPartOfSnippet =
    snippet?.trim()?.split(' ')?.[0]?.split('\n')?.[0] || null;

  return { cfi, snippet, firstPartOfSnippet };
};

/**
 * Parses CFI components needed for character offset calculation
 * @param {string} cfi
 * @returns {{element: Element|null, cfiCharCount: number, cfiNodeStep: number|null}}
 */
const parseCfiComponents = (frameDocument, cfi) => {
  if (!cfi) {
    return { element: null, cfiCharCount: 0, cfiNodeStep: null };
  }

  try {
    const element = cfiLogic.getElementForCfi(frameDocument, cfi);
    const firstElement = cfi.split(',')[1];

    // Parse character count - handle both valid 0 and missing values
    const charCountStr = firstElement?.split(':')?.[1];
    const cfiCharCount =
      charCountStr !== undefined ? parseInt(charCountStr) : 0;

    const firstPartOfPosition = firstElement?.split(':')?.[0]?.split('/');
    const cfiNodeStep = firstPartOfPosition?.[firstPartOfPosition?.length - 1];

    return {
      element,
      cfiCharCount: isNaN(cfiCharCount) ? 0 : cfiCharCount,
      cfiNodeStep: cfiNodeStep ? parseInt(cfiNodeStep) : null,
    };
  } catch (error) {
    console.warn('Failed to parse CFI components:', error);
    return { element: null, cfiCharCount: 0, cfiNodeStep: null };
  }
};

/**
 * Finds the text node matching the CFI node step
 * @param {Document} frameDocument
 * @param {Element} element
 * @param {number} cfiNodeStep
 * @returns {Node|null}
 */
const findTargetNode = (frameDocument, element, cfiNodeStep) => {
  if (!element || cfiNodeStep === null) {
    return null;
  }

  const walker = frameDocument.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
    null,
    false,
  );

  let currentNode;
  let nodeIndex = 0;

  while ((currentNode = walker.nextNode())) {
    const isCurrentBlacklisted = cfiLogic.isBlacklisted(
      currentNode.parentElement,
    );

    if (nodeIndex === cfiNodeStep) {
      if (isCurrentBlacklisted) {
        // Continue walking to find the next non-blacklisted text node
        let nextNode;
        while ((nextNode = walker.nextNode())) {
          const nextIsBlacklisted = cfiLogic.isBlacklisted(
            nextNode.parentElement,
          );
          if (!nextIsBlacklisted && nextNode.textContent.trim() !== '') {
            return nextNode;
          }
        }
        return null;
      }

      // Check if this is a whitespace-only node
      if (currentNode.textContent.trim() === '') {
        // Continue walking to find the next non-whitespace text node
        let nextNode;
        while ((nextNode = walker.nextNode())) {
          const nextIsBlacklisted = cfiLogic.isBlacklisted(
            nextNode.parentElement,
          );
          if (!nextIsBlacklisted && nextNode.textContent.trim() !== '') {
            return nextNode;
          }
        }
        return null;
      }

      return currentNode;
    }
    nodeIndex++;
  }

  return null;
};

/**
 * Finds the character offset for a given node in the word objects
 * @param {Array} wordObjects
 * @param {Node} node
 * @returns {number}
 */
const findNodeCharacterOffset = (wordObjects, node) => {
  if (!node || !Array.isArray(wordObjects)) {
    return 0;
  }

  for (const wordObject of wordObjects) {
    const nodeIncluded = wordObject.nodes?.includes(node);
    const childNodeIncluded = wordObject.nodes?.some(
      (childNode) => childNode.parentElement === node,
    );

    if (nodeIncluded || childNodeIncluded) {
      return wordObject.characterOffset || 0;
    }
  }

  return 0;
};

/**
 * Attempts to find a word object that matches the selection precisely
 * @param {Array} wordObjects
 * @param {number} targetOffset
 * @param {string} firstPartOfSnippet
 * @returns {Object|null}
 */
const findWordByCharacterOffset = (
  wordObjects,
  targetOffset,
  firstPartOfSnippet,
) => {
  if (!Array.isArray(wordObjects) || !firstPartOfSnippet) {
    return null;
  }

  const result = wordObjects.find((word) => {
    const wordEndOffset = word.characterOffset + word.bufferLength;
    const offsetDifference = wordEndOffset - targetOffset;

    const isWithinRange =
      offsetDifference >= 0 && offsetDifference <= word.bufferLength;
    const containsSnippet = word.wordText?.includes(firstPartOfSnippet);

    return isWithinRange && containsSnippet;
  });

  return result || null;
};

/**
 * Attempts to find a word object using a fuzzy search with tolerance
 * @param {Array} wordObjects
 * @param {number} targetOffset
 * @param {string} firstPartOfSnippet
 * @param {number} tolerance
 * @returns {Object|null}
 */
const findWordByFuzzySearch = (
  wordObjects,
  targetOffset,
  firstPartOfSnippet,
  tolerance = 10,
) => {
  if (!Array.isArray(wordObjects) || !firstPartOfSnippet) {
    return null;
  }

  const result = wordObjects.find((word, index, array) => {
    const isWithinTolerance =
      word.characterOffset <= targetOffset + tolerance &&
      array[index + 1]?.characterOffset >= targetOffset - tolerance;

    const containsSnippet = word.wordText?.includes(firstPartOfSnippet);

    return isWithinTolerance && containsSnippet;
  });

  return result || null;
};

/**
 * Main function to calculate character offset from a selected highlight
 * @param {BookDocumentInformation} bookDocumentInformation
 * @param {Document} frameDocument
 * @param {UserContentHighlight} selectedHighlight
 * @returns {number}
 */
const getCharacterOffsetFromSelection = (
  bookDocumentInformation,
  frameDocument,
  selectedHighlight,
) => {
  // Validate inputs (will throw if invalid)
  validateInputs(bookDocumentInformation, frameDocument, selectedHighlight);

  try {
    // Extract highlight information
    const { cfi, firstPartOfSnippet } = extractHighlightInfo(selectedHighlight);

    if (!cfi) {
      return 0;
    }

    // Parse CFI components
    const { element, cfiCharCount, cfiNodeStep } = parseCfiComponents(
      frameDocument,
      cfi,
    );

    if (!element) {
      return 0;
    }

    // Find the target node
    const node = findTargetNode(frameDocument, element, cfiNodeStep);

    if (!node) {
      return 0;
    }

    // Find base character offset for the node
    const nodeCharacterOffset = findNodeCharacterOffset(
      bookDocumentInformation.wordObjects,
      node,
    );

    // If no character offset found for the node, try to find the word directly by snippet
    if (nodeCharacterOffset === 0 && firstPartOfSnippet) {
      // Search for word objects that belong to this specific CFI element and contain the snippet
      const matchingWords = bookDocumentInformation.wordObjects.filter(
        (word) => {
          // Check if this word object's nodes are within our target element
          const isInElement = word.nodes?.some(
            (node) => element.contains(node) || element === node.parentElement,
          );

          // Check if the word contains our snippet
          const containsSnippet = word.wordText
            ?.toLowerCase()
            .includes(firstPartOfSnippet.toLowerCase());

          return isInElement && containsSnippet;
        },
      );

      if (matchingWords.length > 0) {
        // Return the character offset of the first matching word within this element
        return matchingWords[0].characterOffset;
      }
    }

    // Calculate target offset
    const targetOffset = cfiCharCount + nodeCharacterOffset;

    // Try precise match first
    let selectedWord = findWordByCharacterOffset(
      bookDocumentInformation.wordObjects,
      targetOffset,
      firstPartOfSnippet,
    );

    // Fall back to fuzzy search if precise match fails
    if (!selectedWord) {
      selectedWord = findWordByFuzzySearch(
        bookDocumentInformation.wordObjects,
        targetOffset,
        firstPartOfSnippet,
      );
    }

    // Return the character offset or fallback
    return selectedWord?.characterOffset ?? nodeCharacterOffset;
  } catch (error) {
    console.error('Error calculating character offset from selection:', error);
    return 0;
  }
};

export {
  validateInputs,
  extractHighlightInfo,
  parseCfiComponents,
  findTargetNode,
  findNodeCharacterOffset,
  findWordByCharacterOffset,
  findWordByFuzzySearch,
  getCharacterOffsetFromSelection,
};

export default getCharacterOffsetFromSelection;
